import { takeLatest, call, all, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { sectionsApi } from 'services/apiDigitalTicket/sections';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as SectionsActions from './actions';
import { SectionInterface, SectionsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof SectionsActions.listSectionsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando seções' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sectionsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        SectionsActions.listSectionsSuccess({
          sectionsList: response.data.map((section: SectionInterface) => ({
            ...section,
            startStop: section.startStop ? section.startStop?.name : '',
            endStop: section.endStop ? section.endStop?.name : '',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sections - listSections - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.listSectionsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof SectionsActions.getSectionRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando seção' }));
    const { params } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sectionsApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        ...params,
      },
    });

    if (response.status === 200 && response.data.id) {
      yield put(
        SectionsActions.getSectionSuccess({ sectionData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sections - getSection - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.getSectionFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof SectionsActions.filterSectionsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sectionsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SectionsActions.filterSectionsSuccess({
          sectionsList: response.data.map((section: SectionInterface) => ({
            ...section,
            startStop: section.startStop ? section.startStop?.name : '',
            endStop: section.endStop ? section.endStop?.name : '',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sections - filterSections - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.filterSectionsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof SectionsActions.createSectionRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando seção' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sectionsApi.create, {
      data: { ...payload, transportOperatorId: transportOperator.id },
    });

    if (response.status === 200) {
      yield put(
        SectionsActions.createSectionSuccess({ sectionCreated: response.data }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Seção criada com sucesso',
        }),
      );
      yield put(push(PATH.SECTIONS.BASE_URL));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sections - createSection - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.createSectionFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
  meta,
}: ReturnType<typeof SectionsActions.createSectionsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Enviando seções' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const responses = yield all(
      payload.sections.map((section: any) =>
        call(sectionsApi.create, {
          data: { ...section, transportOperatorId: transportOperator.id },
        }),
      ),
    );

    if (responses.every(({ status }: any) => status === 200)) {
      yield put(SectionsActions.createSectionsSuccess());
      yield meta.promise.resolve(responses);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sections - createSections - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.createSectionsFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof SectionsActions.updateSectionRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando seção' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sectionsApi.update, {
      data: { ...payload, transportOperatorId: transportOperator.id },
    });

    if (response.status === 200) {
      yield put(
        SectionsActions.updateSectionSuccess({ sectionData: response.data }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Seção editada com sucesso',
        }),
      );
      yield put(
        SectionsActions.listSectionsRequest({
          body: {
            _includes: 'startStop,endStop',
          },
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sections - updateSection - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.updateSectionFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof SectionsActions.deleteSectionRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Deletando seção' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sectionsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Seção excluída com sucesso',
        }),
      );
      yield put(SectionsActions.deleteSectionSuccess());
      yield put(SectionsActions.listSectionsRequest({}));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('stops - removeSection - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SectionsActions.deleteSectionFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const sectionsSagas = [
  takeLatest(SectionsTypes.LIST_SECTIONS_REQUEST, list),
  takeLatest(SectionsTypes.GET_SECTION_REQUEST, get),
  takeLatest(SectionsTypes.FILTER_SECTIONS_REQUEST, filter),
  takeLatest(SectionsTypes.CREATE_SECTION_REQUEST, create),
  takeLatest(SectionsTypes.CREATE_SECTIONS_REQUEST, createList),
  takeLatest(SectionsTypes.UPDATE_SECTION_REQUEST, update),
  takeLatest(SectionsTypes.DELETE_SECTION_REQUEST, remove),
];
